import { amountText, type BabyPeriods, BOTTLE_UNIT_TITLE, type BottleUnit } from "@babia/checks";
import { Link } from "@tanstack/react-router";
import { lazy, Suspense, useMemo } from "react";
import {
  type PeriodChart,
  periodChart,
  periodMax,
  type TrendKind,
  type Zoom,
  ZOOM_UNIT,
} from "../../lib/periods.ts";
import type { Baby } from "../../lib/queries.ts";
import { clock, shortDuration } from "../../lib/time.ts";
import { cn } from "../../lib/utils.ts";
import { AVERAGE, CHANGES, SLEEP } from "./chart-series.ts";
import { PerBaby } from "./section.tsx";

// ECharts loads with the first chart, not with the log (see trends.tsx).
const PeriodBars = lazy(() => import("./period-bars.tsx").then((m) => ({ default: m.PeriodBars })));

export type PeriodZoom = Exclude<Zoom, "days">;

const quiet = "mt-3 py-3 text-[14px] italic opacity-60";
const HOUR = 3_600_000;

/** "the weeks since birth" or "the months since birth". */
const since = (zoom: PeriodZoom) => `the ${zoom} since birth`;

export function AddBirthday({ many, zoom }: { many: boolean; zoom: PeriodZoom }) {
  return (
    <p className={quiet}>
      Add {many ? "each baby's birthday" : "a birthday"} in{" "}
      <Link to="/settings" hash="family" className="underline underline-offset-2">
        Settings
      </Link>{" "}
      to see {since(zoom)}.
    </p>
  );
}

/** Why a baby has no chart of this kind yet. */
const EMPTY: Record<TrendKind, string> = {
  bottles: "No full day of bottles to average yet. Today and the day logging began are left out.",
  changes: "No full day of changes to average yet. Today and the day logging began are left out.",
  sleep:
    "No whole night of sleep to average yet. The night still going and the night logging began are left out.",
};

/** Every period in the chart as text, newest first: the chart's plain-text twin. */
function PeriodTable({ chart, zoom }: { chart: PeriodChart; zoom: PeriodZoom }) {
  return (
    <details className="group mt-2">
      <summary className="ghost-btn -ml-2 inline-flex min-h-[44px] cursor-pointer items-center px-2 text-[14px]">
        <span className="group-open:hidden">Show the table</span>
        <span className="hidden group-open:inline">Hide the table</span>
      </summary>
      <table className="w-full border-collapse text-left text-[13px] tabular-nums">
        <thead>
          <tr className="border-b border-ink text-[11px] uppercase tracking-[0.06em]">
            <th className="py-1.5 pr-2 font-normal opacity-60">
              {zoom === "weeks" ? "Week" : "Month"}
            </th>
            {chart.heads.map((h, i) => (
              <th
                key={h}
                className={cn(
                  "py-1.5 text-right font-normal opacity-60",
                  i < chart.heads.length - 1 && "pr-2",
                )}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[...chart.rows].reverse().map((r) => (
            <tr key={r.n} className="border-t border-rule">
              <th scope="row" className="py-1.5 pr-2 font-normal">
                {r.name}
              </th>
              {r.cells.map((c, i) => (
                <td
                  key={chart.heads[i]}
                  className={cn("py-1.5 text-right", i < r.cells.length - 1 && "pr-2")}
                >
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  );
}

/**
 * The Trends section's Weeks and Months: each baby's average a day (a night,
 * for sleep) week by week or month by month of life, from @babia/checks'
 * counted days. Only for a baby with a birthday. Twins share one scale.
 */
export function PeriodTrends({
  babies,
  periods,
  kind,
  zoom,
  unit,
  loading,
  error,
  staleSince,
}: {
  /** The babies shown: one on a phone with tabs. */
  babies: Baby[];
  /** Every baby's periods (absent for a baby with no birthday), for the shared scale. */
  periods: ReadonlyMap<string, BabyPeriods>;
  kind: TrendKind;
  zoom: PeriodZoom;
  /** The unit bottles show in. */
  unit: BottleUnit;
  loading: boolean;
  /** Why the periods never loaded. */
  error: string | null;
  /** When the periods shown were loaded, if a refresh since then failed. */
  staleSince: number | null;
}) {
  const period = ZOOM_UNIT[zoom];
  const charts = useMemo(
    () => new Map([...periods].map(([id, p]) => [id, periodChart(kind, p, period, unit)])),
    [periods, kind, period, unit],
  );
  const max = periodMax([...charts.values()], kind === "sleep" ? 3 : undefined);
  const adjective = period === "week" ? "weekly" : "monthly";
  const what = {
    bottles: `${BOTTLE_UNIT_TITLE[unit]} a day`,
    changes: "Changes a day",
    sleep: "Sleep a night",
  }[kind];

  return (
    <>
      {error ? (
        <p className="mt-3 text-[14px] text-accent-2">
          Couldn't load {since(zoom)} — {error}.
        </p>
      ) : staleSince !== null ? (
        <p className="mt-3 text-[13px] opacity-70">
          Couldn't refresh the {zoom} — showing them as of {clock(new Date(staleSince))}.
        </p>
      ) : null}
      <PerBaby babies={babies}>
        {(b) => {
          if (b.birthDate === null) return <AddBirthday many={false} zoom={zoom} />;
          if (loading) return <p className={quiet}>Loading {since(zoom)}…</p>;
          if (error) return null;
          const chart = charts.get(b.id);
          if (!chart?.rows.length) return <p className={quiet}>{EMPTY[kind]}</p>;
          return (
            // A new zoom starts fresh: the newest bar, the table closed.
            <div key={zoom}>
              <Suspense fallback={<div className="h-[210px]" aria-hidden="true" />}>
                <PeriodBars
                  rows={chart.rows}
                  series={kind === "bottles" ? AVERAGE : kind === "changes" ? CHANGES : SLEEP}
                  max={max}
                  label={`${what} by ${period} of life`}
                  valueText={
                    kind === "bottles"
                      ? (v) => amountText(v, unit)
                      : kind === "changes"
                        ? (v) => String(Math.round(v * 10) / 10)
                        : (v) => shortDuration(v * HOUR)
                  }
                  tickText={kind === "sleep" ? (v) => `${v}h` : undefined}
                  chart={`${adjective} ${kind}`}
                />
              </Suspense>
              <PeriodTable chart={chart} zoom={zoom} />
            </div>
          );
        }}
      </PerBaby>
    </>
  );
}
